import { type DragEvent, useState } from "react";

import { getPointerDropDirection } from "~/v3-poc/utils/dropDirection";
import {
  isOrdersWidgetColumnId,
  type OrdersWidgetColumnId,
} from "./OrdersWidget.columns";
import type {
  OrdersColumnDropDirection,
  ReorderOrdersColumns,
} from "./useOrdersColumnVisibility";

export const COLUMN_REORDER_DRAG_TYPE = "application/x-atd-orders-column";

export interface UseOrdersColumnsPopoverControllerParams {
  onReorderColumns: ReorderOrdersColumns;
}

export const useOrdersColumnsPopoverController = ({
  onReorderColumns,
}: UseOrdersColumnsPopoverControllerParams) => {
  const [draggingColumnId, setDraggingColumnId] =
    useState<OrdersWidgetColumnId | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{
    columnId: OrdersWidgetColumnId;
    direction: OrdersColumnDropDirection;
  } | null>(null);

  const clearColumnDrag = () => {
    setDraggingColumnId(null);
    setDropIndicator(null);
  };

  const handleDragStart = (
    event: DragEvent<HTMLElement>,
    columnId: OrdersWidgetColumnId,
  ) => {
    event.dataTransfer.setData(COLUMN_REORDER_DRAG_TYPE, columnId);
    event.dataTransfer.setData("text/plain", columnId);
    event.dataTransfer.effectAllowed = "move";
    const row = event.currentTarget.closest("[data-orders-column-row]");
    if (row instanceof HTMLElement) {
      const rect = row.getBoundingClientRect();
      event.dataTransfer.setDragImage(
        row,
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
    }
    setDraggingColumnId(columnId);
    setDropIndicator(null);
  };

  const handleDragEnd = () => {
    clearColumnDrag();
  };

  const handleDragOver = (
    event: DragEvent<HTMLDivElement>,
    targetColumnId: OrdersWidgetColumnId,
    isValidTarget: boolean,
  ) => {
    if (!draggingColumnId) {
      return;
    }
    if (!isValidTarget || draggingColumnId === targetColumnId) {
      setDropIndicator(null);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const direction = getPointerDropDirection(
      "vertical",
      event.clientX,
      event.clientY,
      rect,
    );
    setDropIndicator((current) =>
      current?.columnId === targetColumnId && current.direction === direction
        ? current
        : { columnId: targetColumnId, direction },
    );
  };

  const handleDrop = (
    event: DragEvent<HTMLDivElement>,
    targetColumnId: OrdersWidgetColumnId,
  ) => {
    event.preventDefault();
    const sourceColumnId =
      event.dataTransfer.getData(COLUMN_REORDER_DRAG_TYPE) ||
      event.dataTransfer.getData("text/plain");
    const direction =
      dropIndicator?.columnId === targetColumnId
        ? dropIndicator.direction
        : getPointerDropDirection(
            "vertical",
            event.clientX,
            event.clientY,
            event.currentTarget.getBoundingClientRect(),
          );
    clearColumnDrag();
    if (
      !isOrdersWidgetColumnId(sourceColumnId) ||
      sourceColumnId === targetColumnId
    ) {
      return;
    }
    onReorderColumns(sourceColumnId, targetColumnId, direction);
  };

  return {
    draggingColumnId,
    dropIndicator,
    clearColumnDrag,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
  };
};
